"use client";

import React, {
	createContext,
	useCallback,
	useContext,
	useEffect,
	useState,
	useSyncExternalStore,
} from "react";
import {
	Currency,
	DEFAULT_CURRENCY,
	detectUserCurrency,
	isValidCurrency,
} from "@/lib/utils/currency";

export const CURRENCY_CHANGE_EVENT = "techxeed-currency-change";

/**
 * Detects currency synchronously from `window.__USER_CURRENCY__` (blocking script),
 * cookie, or localStorage — runs before / during first client render.
 */
export function detectCurrencySync(): Currency {
	if (typeof window === "undefined") {
		return DEFAULT_CURRENCY;
	}

	const globalCurrency = (window as Window & { __USER_CURRENCY__?: string }).__USER_CURRENCY__;
	if (globalCurrency && isValidCurrency(globalCurrency)) {
		return globalCurrency as Currency;
	}

	return detectUserCurrency();
}

function subscribeToCurrency(onStoreChange: () => void) {
	if (typeof window === "undefined") {
		return () => {};
	}
	window.addEventListener(CURRENCY_CHANGE_EVENT, onStoreChange);
	window.addEventListener("storage", onStoreChange);
	return () => {
		window.removeEventListener(CURRENCY_CHANGE_EVENT, onStoreChange);
		window.removeEventListener("storage", onStoreChange);
	};
}

const CurrencyContext = createContext<Currency>(DEFAULT_CURRENCY);

type CurrencyProviderProps = {
	children: React.ReactNode;
	/** From `userCurrency` cookie on the server so SSR matches hydration. */
	initialCurrency?: Currency;
};

export function CurrencyProvider({
	children,
	initialCurrency = DEFAULT_CURRENCY,
}: CurrencyProviderProps) {
	const getServerSnapshot = useCallback(() => initialCurrency, [initialCurrency]);
	const storeCurrency = useSyncExternalStore(
		subscribeToCurrency,
		detectCurrencySync,
		getServerSnapshot,
	);

	/**
	 * First client render must match SSR (cookie / default). The blocking script may
	 * already have set GBP via locale before React hydrates — if we used that during
	 * hydration we'd get $365 vs £278 mismatches. After mount, switch to live detection.
	 */
	const [ready, setReady] = useState(false);
	useEffect(() => {
		setReady(true);
	}, []);

	const currency = ready ? storeCurrency : initialCurrency;

	useEffect(() => {
		document.body?.setAttribute("data-currency", currency);
	}, [currency]);

	return <CurrencyContext.Provider value={currency}>{children}</CurrencyContext.Provider>;
}

export function useCurrency(): Currency {
	return useContext(CurrencyContext);
}
